// The review screen: a flat, sorted list of candidate words. Tags are editable
// inline; words can be crossed out (reversible) or have their conflicts resolved.
// A sticky footer carries the conflict counter + jump controls, a (disabled,
// "bientôt disponible") draft button, and Valider — which is blocked while any
// conflict is unresolved or no active word remains.
import { BulkConfirmDialog } from '@/components/words/bulk-add/bulk-confirm-dialog';
import { BulkConflictBar } from '@/components/words/bulk-add/bulk-conflict-bar';
import { BulkConflictDialog } from '@/components/words/bulk-add/bulk-conflict-dialog';
import { BulkReviewRow } from '@/components/words/bulk-add/bulk-review-row';
import { SaveIndicator } from '@/components/words/bulk-add/bulk-save-indicator';
import { BulkSummaryDialog } from '@/components/words/bulk-add/bulk-summary-dialog';
import { WordShellTopbar } from '@/components/words/word-shell-topbar';
import { accentStyle, noyauDotKey } from '@/lib/bulk-add/accent';
import {
  type GlobalResolutionKind,
  type SubmitSummary,
  useBulkAdd,
} from '@/lib/bulk-add/bulk-session';
import { detectConflicts, firstConflictFor } from '@/lib/bulk-add/conflicts';
import type { BulkSession, BulkWord } from '@/lib/bulk-add/types';
import { useEffect, useMemo, useState } from 'react';

/** A pre-resolution snapshot captured just before the "apply to all" bar runs, for its Undo. */
type GlobalResolutionSnapshot = {
  kind: GlobalResolutionKind;
  words: BulkWord[];
  resolvedKeys: string[];
};

interface BulkReviewProps {
  session: BulkSession;
}

type DialogState = 'none' | 'cancel' | 'submit';

/** The bulk-add review + submit screen for one session. */
export function BulkReview({ session }: BulkReviewProps) {
  const bulk = useBulkAdd();
  const [sortAsc, setSortAsc] = useState(true);
  const [conflictWordId, setConflictWordId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>('none');
  const [summary, setSummary] = useState<SubmitSummary | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [jumpIndex, setJumpIndex] = useState(0);
  const [globalResolution, setGlobalResolution] = useState<GlobalResolutionSnapshot | null>(null);

  const resolved = useMemo(() => new Set(session.resolvedKeys), [session.resolvedKeys]);
  const conflicts = useMemo(
    () => detectConflicts(session.words, resolved),
    [session.words, resolved],
  );

  // The green "resolved" banner state is only valid until a conflict shows up
  // again (e.g. a re-analysis on reopen surfaces a fresh one) — at that point
  // the captured snapshot no longer reflects "just before the last global
  // resolution", so drop it back to the normal red/pending bar.
  useEffect(() => {
    if (conflicts.length > 0) setGlobalResolution(null);
  }, [conflicts.length]);

  function handleApplyAll(kind: GlobalResolutionKind) {
    setGlobalResolution({ kind, words: session.words, resolvedKeys: session.resolvedKeys });
    bulk.resolveAll(session.id, kind);
  }

  function handleUndoAll() {
    if (!globalResolution) return;
    bulk.restoreSnapshot(session.id, globalResolution.words, globalResolution.resolvedKeys);
    setGlobalResolution(null);
  }

  // Unique row ids touched by a conflict, for the prev/next jump controls.
  const conflictAnchors = useMemo(() => {
    const ids = new Set<string>();
    for (const c of conflicts) {
      if (c.kind === 'batch') {
        ids.add(c.a.id);
        ids.add(c.b.id);
      } else {
        ids.add(c.word.id);
      }
    }
    return [...ids];
  }, [conflicts]);

  const conflictedIds = useMemo(() => new Set(conflictAnchors), [conflictAnchors]);
  const activeCount = session.words.filter((w) => !w.excluded).length;
  const canSubmit = conflicts.length === 0 && activeCount > 0;
  const noyauKey = noyauDotKey(session.noyau);

  // Sort by title only (A→Z / Z→A). Title-based ordering never changes on a tag
  // edit or cross-out, so the list stays put while editing (not "live" reorder).
  const rows = useMemo(() => {
    const copy = [...session.words];
    copy.sort((a, b) => a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' }));
    if (!sortAsc) copy.reverse();
    return copy;
  }, [session.words, sortAsc]);

  const openConflict = conflictWordId ? firstConflictFor(conflictWordId, conflicts) : undefined;

  // Scroll the next conflicting row into view, cycling through them.
  function jumpToNextConflict() {
    if (conflictAnchors.length === 0) return;
    const idx = jumpIndex % conflictAnchors.length;
    const id = conflictAnchors[idx];
    setJumpIndex(idx + 1);
    if (id) document.getElementById(`bulk-row-${id}`)?.scrollIntoView({ block: 'center' });
  }

  async function handleSubmit() {
    setDialog('none');
    setSubmitting(true);
    const result = await bulk.submit(session.id);
    setSubmitting(false);
    setSummary(result);
  }

  // Closing the summary: full success discards the session; a partial failure
  // returns to the (now error-flagged) session for a retry.
  function handleSummaryClose() {
    const failed = summary?.failed ?? 0;
    setSummary(null);
    if (failed === 0) bulk.finishSession(session.id);
  }

  // Zero words parsed → friendly empty state back to the input page.
  if (session.words.length === 0) {
    return (
      <>
        <WordShellTopbar />
        <div className="bulk" style={accentStyle(session.noyau)}>
          <div className="bulk-empty">
            <span className="material-symbols-rounded bulk-empty__icon" aria-hidden="true">
              search_off
            </span>
            <h1 className="bulk-empty__title">Aucun mot détecté</h1>
            <p className="bulk-empty__text">Le texte collé ne contenait aucun mot exploitable.</p>
            <button
              type="button"
              className="bulk-btn bulk-btn--primary"
              onClick={() => {
                bulk.cancelSession(session.id);
                bulk.startNew();
              }}
            >
              Modifier l'entrée
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <WordShellTopbar />
      <div className="bulk bulk-review" style={accentStyle(session.noyau)}>
        <header className="bulk-review__head">
          <div className="bulk-review__heading">
            <h1 className="bulk-review__title">{session.name}</h1>
            <p className="bulk-review__meta">
              {activeCount} mot{activeCount > 1 ? 's' : ''} à ajouter
            </p>
          </div>
          <button
            type="button"
            className="bulk-sort"
            onClick={() => setSortAsc((v) => !v)}
            aria-label={`Trier ${sortAsc ? 'de Z à A' : 'de A à Z'}`}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              {sortAsc ? 'arrow_downward' : 'arrow_upward'}
            </span>
            {sortAsc ? 'A → Z' : 'Z → A'}
          </button>
        </header>

        {(conflicts.length > 0 || globalResolution) && (
          <BulkConflictBar
            count={conflicts.length}
            onApply={handleApplyAll}
            resolvedKind={conflicts.length === 0 ? (globalResolution?.kind ?? null) : null}
            onUndo={handleUndoAll}
          />
        )}

        <ul className="bulk-list">
          {rows.map((word) => (
            <BulkReviewRow
              key={word.id}
              word={word}
              noyauKey={noyauKey}
              inConflict={conflictedIds.has(word.id)}
              onTagsChange={(tags) => bulk.setWordTags(session.id, word.id, tags)}
              onToggleExclude={() => bulk.toggleExclude(session.id, word.id)}
              onResolveConflict={() => setConflictWordId(word.id)}
            />
          ))}
        </ul>

        <footer className="bulk-footer">
          <div className="bulk-footer__conflicts">
            {conflicts.length > 0 ? (
              <button type="button" className="bulk-footer__jump" onClick={jumpToNextConflict}>
                <span className="material-symbols-rounded" aria-hidden="true">
                  error
                </span>
                {conflicts.length} conflit{conflicts.length > 1 ? 's' : ''} à résoudre
              </button>
            ) : (
              <span className="bulk-footer__ok">Aucun conflit</span>
            )}
          </div>
          <div className="bulk-footer__actions">
            <SaveIndicator status={bulk.saveStatus[session.id]} />
            <button
              type="button"
              className="bulk-btn bulk-btn--ghost"
              onClick={() => setDialog('cancel')}
            >
              Annuler
            </button>
            <button
              type="button"
              className="bulk-btn bulk-btn--outline"
              onClick={() => void bulk.saveAndClose(session.id)}
            >
              Enregistrer et quitter
            </button>
            <button
              type="button"
              className="bulk-btn bulk-btn--primary"
              disabled={!canSubmit || submitting}
              onClick={() => setDialog('submit')}
            >
              {submitting ? 'Ajout…' : "Valider l'ajout"}
            </button>
          </div>
        </footer>
      </div>

      {openConflict && (
        <BulkConflictDialog
          conflict={openConflict}
          noyau={session.noyau}
          onResolve={(resolution) => {
            bulk.resolve(session.id, resolution);
            setConflictWordId(null);
          }}
          onClose={() => setConflictWordId(null)}
        />
      )}

      {dialog === 'cancel' && (
        <BulkConfirmDialog
          title="Annuler la revue"
          message="Cette session sera supprimée. Êtes-vous sûr ?"
          confirmLabel="Supprimer la session"
          danger
          onConfirm={() => {
            setDialog('none');
            bulk.cancelSession(session.id);
          }}
          onClose={() => setDialog('none')}
        />
      )}

      {dialog === 'submit' && (
        <BulkConfirmDialog
          title="Valider l'ajout"
          message={`Ajouter ${activeCount} mot${activeCount > 1 ? 's' : ''} ?`}
          confirmLabel="Ajouter"
          onConfirm={() => void handleSubmit()}
          onClose={() => setDialog('none')}
        />
      )}

      {summary && <BulkSummaryDialog summary={summary} onClose={handleSummaryClose} />}
    </>
  );
}
